import { z } from 'zod';
import { IsoTimestamp, SchoolId, UserId } from './common.js';
/**
 * Effective server-side cap on one atomic issue batch. The request schema
 * allows 200 ids, but the Firestore transaction cannot write more than this, so
 * the UI must stop the teacher here rather than let them select 150 and eat a
 * 400. Defined once, in the shared package, so the picker and the store cannot
 * disagree about it.
 */
export const MAX_ACCESS_CODE_BATCH_STUDENTS = 100;
/**
 * Safety bound on how many candidate user documents the picker's roster search
 * reads before giving up. Lives here so the API's scan and the picker's
 * "refine your search" copy cannot state different numbers.
 */
export const ELIGIBLE_STUDENTS_SCAN_CAP = 10_000;
export const AccessCodeStatus = z.enum(['active', 'consumed', 'revoked', 'expired']);
export const AccessCode = z.string().regex(/^[A-HJ-NP-Z2-9]{16}$/);
export const AccessCodeExchangeRequest = z.object({
    studentIdentifier: z.string().trim().min(1).max(128),
    code: AccessCode,
});
/**
 * Ask the API to lift a student's sign-in lock once every access code that
 * placed it has lapsed. The endpoint always answers `{ ok: true }` — the
 * response never reveals whether the email exists or a lock was released.
 */
export const AccessCodeUnlockRequest = z.object({
    email: z.string().trim().email().max(254).transform((value) => value.toLowerCase()),
});
export const AccessCodeExchangeResponse = z.object({
    customToken: z.string().min(1),
});
const AccessCodeStudentId = UserId.refine((value) => /^[A-Za-z0-9:_-]+$/.test(value), {
    message: 'student ID contains an unsafe path character',
});
export const IssueAccessCodeRequest = z.object({
    studentIds: z.array(AccessCodeStudentId).min(1).max(200).refine((studentIds) => new Set(studentIds).size === studentIds.length, 'student IDs must be unique'),
    expiresAt: IsoTimestamp,
    reason: z.string().trim().min(3).max(500),
});
export const IssuedAccessCode = z.object({
    id: z.string().min(1).max(128),
    batchId: z.string().uuid(),
    schoolId: SchoolId,
    studentId: UserId,
    studentName: z.string().min(1).max(200),
    code: AccessCode,
    expiresAt: IsoTimestamp,
    issuedAt: IsoTimestamp,
});
export const AccessCodeRecord = z.object({
    id: z.string().min(1).max(128),
    batchId: z.string().uuid(),
    schoolId: SchoolId,
    studentId: UserId,
    studentName: z.string().min(1).max(200),
    verifier: z.string().min(1).max(256).nullable(),
    /** Server-only sealed value for same-code reissue; absent on legacy records. */
    codeEnc: z.string().min(1).max(256).optional(),
    status: AccessCodeStatus,
    expiresAt: IsoTimestamp,
    issuedAt: IsoTimestamp,
    issuedBy: UserId,
    consumedAt: IsoTimestamp.optional(),
    consumedBy: UserId.optional(),
    revokedAt: IsoTimestamp.optional(),
    revokedBy: UserId.optional(),
    revokeReason: z.string().max(500).optional(),
    reason: z.string().min(3).max(500),
    reissuedFromId: z.string().min(1).max(128).optional(),
});
export const PublicAccessCodeRecord = AccessCodeRecord.omit({ verifier: true, codeEnc: true }).extend({
    classroomNames: z.array(z.string().min(1).max(200)),
});
export const RevokeAccessCodeRequest = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const ReissueAccessCodeRequest = RevokeAccessCodeRequest.extend({
    expiresAt: IsoTimestamp.optional(),
});
export const ResetAccessCodeRequest = z.object({
    studentId: AccessCodeStudentId,
    expiresAt: IsoTimestamp,
    reason: z.string().trim().min(3).max(500),
});
/**
 * One row of the access-code picker's roster. `classroomNames` exists so a
 * teacher can tell two same-named students apart — it is scoped to the
 * caller's own classrooms, so it never leaks the school's structure.
 */
export const EligibleAccessCodeStudent = z.object({
    id: UserId,
    displayName: z.string().min(1).max(200),
    studentNumber: z.string().min(1).max(128).nullable(),
    classroomNames: z.array(z.string().max(200)).max(50),
});
export const EligibleAccessCodeStudentsResponse = z.object({
    students: z.array(EligibleAccessCodeStudent),
    /**
     * Exact number of matching students when the scan reached the end of the
     * roster, `null` when it stopped at the page boundary — the scan deliberately
     * does not walk the whole student body just to produce a count, so a caller
     * must treat `null` as "at least `students.length`, and `hasMore` is true".
     */
    total: z.number().int().min(0).nullable(),
    hasMore: z.boolean(),
    truncated: z.boolean(),
    /**
     * Why the list is empty, when it is empty for a reason the teacher can act
     * on. `no-classrooms` means the caller has no assigned classrooms — the
     * picker explains that instead of showing an empty dropdown.
     */
    reason: z.enum(['no-classrooms']).nullable(),
});
//# sourceMappingURL=access-code.js.map